import { useState } from "react";
import {
    EmissionsSourceRequiredEvidenceOrderBy,
    RequiredEvidenceForEmissionsSource,
    RequiredEvidenceStatusFilter,
} from "../../api/models/RequiredEvidence";
import { useRequiredEvidenceForEmissionsSources } from "../../api/required-evidence";
import CreateEvidenceDialog from "./CreateEvidenceDialog";
import RequiredEvidenceTable from "./RequiredEvidenceTable";
import EditEvidenceDialog from "./EditEvidenceDialog";
import RemoveRequiredEvidenceDialog from "./RemoveRequiredEvidenceDialog";
import AddRequiredEvidenceDialog from "./AddRequiredEvidenceTypeDialog";
import Button from "../Button";
import SectionTitle from "../SectionTitle";
import {
    useAddEmissionsSourceRequiredEvidence,
    useRemoveEmissionsSourceRequiredEvidence,
} from "../../api/emissions-sources";
import Pagination from "../Pagination";
import RequiredEmissionsSourceEvidenceFilter from "./RequiredEvidenceFilter";
import { useStateParams } from "../../hooks/useStateParams";

const statusFilterOptions: {
    value: RequiredEvidenceStatusFilter;
    label: string;
}[] = [
    { value: "MissingOnly", label: "Missing" },
    { value: "MissingAndExpired", label: "Missing and expired" },
    { value: "ExpiringWithin7Days", label: "Expiring within 7 days" },
    { value: "ExpiringWithin30Days", label: "Expiring within 30 days" },
    { value: "ExpiringWithin90Days", label: "Expiring within 90 days" },
    { value: "All", label: "All" },
];

const defaultStatusFilterOption = statusFilterOptions[3];

export type RequiredEmissionsSourceEvidenceTableProps = {
    emissionsSourceId?: number;
    activityDataId?: number;
    defaultStatusFilter?: RequiredEvidenceStatusFilter;
    noEvidenceMessage: string;
    readonly?: boolean;
    hideLinkToEmissionsSource?: boolean;
    title?: string;
};

const RequiredEmissionsSourceEvidenceTable = ({
    emissionsSourceId,
    activityDataId,
    defaultStatusFilter,
    noEvidenceMessage,
    readonly,
    hideLinkToEmissionsSource,
    title,
}: RequiredEmissionsSourceEvidenceTableProps) => {
    const [statusFilter, setStatusFilter] =
        useStateParams<RequiredEvidenceStatusFilter>(
            defaultStatusFilter ?? defaultStatusFilterOption.value,
            "statusFilter",
            (s) => s,
            (s) => s as RequiredEvidenceStatusFilter,
            true
        );

    const [evidenceTypeIdToBeAdded, setEvidenceTypeIdToBeAdded] =
        useState<number>(-1);
    const [evidenceTypeNameToBeAdded, setEvidenceTypeNameToBeAdded] =
        useState<string>("");
    const [
        emissionsSourceIdToAddEvidenceTo,
        setEmissionsSourceIdToAddEvidenceTo,
    ] = useState<number>();
    const [
        emissionsSourceNameToAddEvidenceTo,
        setEmissionsSourceNameToAddEvidenceTo,
    ] = useState<string>();

    const [
        emissionsSourceIdToAddRequiredEvidenceTypeTo,
        setEmissionsSourceIdToAddRequiredEvidenceTypeTo,
    ] = useState<number>(-1);

    const [
        requiredEvidenceTypeIdToBeRemoved,
        setRequiredEvidenceTypeIdToBeRemoved,
    ] = useState<number>(-1);
    const [
        emissionsSourceIdToRemoveRequiredEvidenceTypeFrom,
        setEmissionsSourceIdToRemoveRequiredEvidenceTypeFrom,
    ] = useState<number>(-1);

    const [evidenceToEditId, setEvidenceToEditId] = useState<number>();
    const [evidenceToEditValidFromDate, setEvidenceToEditValidFromDate] =
        useState<Date>();
    const [evidenceToEditValidToDate, setEvidenceToEditValidToDate] =
        useState<Date>();

    const {
        requiredEvidence,
        isLoading,
        refetch,
        pageNumber,
        pageSize,
        totalCount,
        totalPages,
        hasPreviousPage,
        hasNextPage,
        evidenceTypeIds,
        searchTerm,
        orderBy,
        sortOrder,
        setPageNumber,
        setPageSize,
        setEvidenceTypeIds,
        setSearchTerm,
        setOrderBy,
        setSortOrder,
    } = useRequiredEvidenceForEmissionsSources(statusFilter, {
        emissionsSourceId: emissionsSourceId,
        activityDataId: activityDataId,
    });

    const {
        requiredEvidence: allRequiredEvidence,
        refetch: refetchAllRequiredEvidence,
    } = useRequiredEvidenceForEmissionsSources("All", {
        emissionsSourceId: emissionsSourceId,
        initialPageSize: 100,
        ignoreFilteringAndSorting: true,
    });

    const requiredEvidenceTypeIds = allRequiredEvidence
        ? allRequiredEvidence.map(
              (requiredEvidenceType) => requiredEvidenceType.evidenceTypeId
          )
        : [];

    const handleAddClick = (rowData: RequiredEvidenceForEmissionsSource) => {
        setEvidenceTypeIdToBeAdded(rowData.evidenceTypeId);
        setEvidenceTypeNameToBeAdded(rowData.evidenceTypeName);
        setEmissionsSourceIdToAddEvidenceTo(rowData.emissionsSourceId);
        setEmissionsSourceNameToAddEvidenceTo(rowData.emissionsSourceName);
    };

    const closeLinkEvidenceDialog = (refetchEvidence: boolean) => {
        if (refetchEvidence) refetch();

        setEvidenceTypeIdToBeAdded(-1);
        setEvidenceTypeNameToBeAdded("");
        setEmissionsSourceIdToAddEvidenceTo(undefined);
        setEmissionsSourceNameToAddEvidenceTo(undefined);
    };

    const handleEditEvidenceClick = (
        rowData: RequiredEvidenceForEmissionsSource
    ) => {
        const {
            currentEvidenceId,
            currentEvidenceValidFromDate,
            currentEvidenceValidToDate,
        } = rowData;

        setEvidenceToEditId(currentEvidenceId);
        setEvidenceToEditValidFromDate(
            currentEvidenceValidFromDate
                ? new Date(currentEvidenceValidFromDate)
                : undefined
        );
        setEvidenceToEditValidToDate(
            currentEvidenceValidToDate
                ? new Date(currentEvidenceValidToDate)
                : undefined
        );
    };

    const closeEditEvidenceDialog = (refetchEvidence: boolean) => {
        if (refetchEvidence) refetch();

        setEvidenceToEditId(undefined);
        setEvidenceToEditValidFromDate(undefined);
        setEvidenceToEditValidToDate(undefined);
    };

    const handleAddRequiredEvidenceTypeClick = () => {
        setEmissionsSourceIdToAddRequiredEvidenceTypeTo(emissionsSourceId as number);
    };

    const closeAddRequiredEvidenceTypeDialog = (refetchEvidence: boolean) => {
        if (refetchEvidence) {
            refetch();
            refetchAllRequiredEvidence();
        }

        setEmissionsSourceIdToAddRequiredEvidenceTypeTo(-1);
    };

    const handleRemoveRequiredEvidenceTypeClick = (
        rowData: RequiredEvidenceForEmissionsSource
    ) => {
        const { evidenceTypeId, emissionsSourceId } = rowData;

        setRequiredEvidenceTypeIdToBeRemoved(evidenceTypeId);
        setEmissionsSourceIdToRemoveRequiredEvidenceTypeFrom(emissionsSourceId);
    };

    const closeRemoveRequiredEvidenceTypeDialog = (
        refetchEvidence: boolean
    ) => {
        if (refetchEvidence) {
            refetch();
            refetchAllRequiredEvidence();
        }

        setRequiredEvidenceTypeIdToBeRemoved(-1);
        setEmissionsSourceIdToRemoveRequiredEvidenceTypeFrom(-1);
    };

    const handleLinkClick = (url: string) => {
        const normalizedUrl = url.startsWith("http") ? url : `https://${url}`;
        window.open(normalizedUrl, "_blank")?.focus();
    };

    const handleOnOrderBy = (newOrderBy: string) => {
        if (newOrderBy === orderBy) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(newOrderBy as EmissionsSourceRequiredEvidenceOrderBy);
            setSortOrder("asc");
        }
    };

    const [addRequiredEvidenceType, isAddingRequiredEvidenceType] =
        useAddEmissionsSourceRequiredEvidence(
            emissionsSourceIdToAddRequiredEvidenceTypeTo
        );
    const [removeRequiredEvidenceType, isRemovingRequiredEvidenceType] =
        useRemoveEmissionsSourceRequiredEvidence(
            emissionsSourceIdToRemoveRequiredEvidenceTypeFrom,
            requiredEvidenceTypeIdToBeRemoved
        );

    return (
        <>
            {emissionsSourceId && (
                <SectionTitle title={title ? title : ""}>
                    <Button
                        variant="outline"
                        onClick={handleAddRequiredEvidenceTypeClick}
                    >
                        Add required evidence type
                    </Button>
                </SectionTitle>
            )}
            {!emissionsSourceId && (
                <RequiredEmissionsSourceEvidenceFilter
                    statusFilterOptions={statusFilterOptions}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    orderBy={orderBy}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    evidenceTypeIds={evidenceTypeIds}
                    setEvidenceTypeIds={setEvidenceTypeIds}
                    sortOrder={sortOrder}
                />
            )}
            {isLoading ? (
                <p className="py-10 text-center">Loading...</p>
            ) : (
                <div className="py-6">
                    <RequiredEvidenceTable
                        isLoading={isLoading}
                        requiredEvidence={requiredEvidence}
                        hideLinkToEmissionsSource={hideLinkToEmissionsSource}
                        showEmissionsSourceColumn={!emissionsSourceId}
                        activityDataId={activityDataId}
                        noEvidenceMessage={noEvidenceMessage}
                        readonly={readonly}
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onOrderBy={handleOnOrderBy}
                        handleLinkClick={handleLinkClick}
                        handleAddClick={handleAddClick}
                        handleEditClick={handleEditEvidenceClick}
                        handleRemoveEvidenceTypeClick={
                            handleRemoveRequiredEvidenceTypeClick
                        }
                    />
                    {requiredEvidence.length > 0 && (
                        <Pagination
                            setPageNumber={setPageNumber}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            totalCount={totalCount}
                            totalPages={totalPages}
                            pageNumber={pageNumber}
                            hasPreviousPage={hasPreviousPage}
                            hasNextPage={hasNextPage}
                        />
                    )}
                    <CreateEvidenceDialog
                        isOpen={evidenceTypeIdToBeAdded !== -1}
                        onClose={closeLinkEvidenceDialog}
                        evidenceTypeId={evidenceTypeIdToBeAdded}
                        evidenceTypeName={evidenceTypeNameToBeAdded}
                        emissionsSourceId={emissionsSourceIdToAddEvidenceTo}
                        emissionsSourceName={emissionsSourceNameToAddEvidenceTo}
                    />
                    <EditEvidenceDialog
                        isOpen={!!evidenceToEditId}
                        onClose={closeEditEvidenceDialog}
                        currentEvidenceId={evidenceToEditId}
                        validFromDate={evidenceToEditValidFromDate}
                        validToDate={evidenceToEditValidToDate}
                    />
                    <RemoveRequiredEvidenceDialog
                        isOpen={requiredEvidenceTypeIdToBeRemoved !== -1}
                        onClose={closeRemoveRequiredEvidenceTypeDialog}
                        request={removeRequiredEvidenceType}
                        isSubmitting={isRemovingRequiredEvidenceType}
                    />
                    <AddRequiredEvidenceDialog
                        isOpen={
                            emissionsSourceIdToAddRequiredEvidenceTypeTo !== -1
                        }
                        onClose={closeAddRequiredEvidenceTypeDialog}
                        request={addRequiredEvidenceType}
                        isSubmitting={isAddingRequiredEvidenceType}
                        requiredEvidenceTypes={requiredEvidenceTypeIds}
                    />
                </div>
            )}
        </>
    );
};

export default RequiredEmissionsSourceEvidenceTable;
